import { useState, useEffect, useCallback } from 'react';
import { parentCommunicationAPI } from '../services/api';
import { Logger } from '../utils/logger';

export interface ClassTeacherContact {
  /** Employee.UserId - the identity id Chat's sender/receiver columns store. */
  id: string;
  employeeId: string;
  name: string;
  isClassTeacher: boolean;
  subjects: string[];
  lastMessageText?: string;
  lastMessageDate?: string;
  unreadCount: number;
}

interface UseClassTeachersResult {
  teachers: ClassTeacherContact[];
  /** The (first) teacher with isClassTeacher=true, if any. */
  classTeacher: ClassTeacherContact | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Every teacher a parent may message about a class, in one call - the identity id
 * (Employee.UserId), name, subjects taught, and whether they're the class teacher.
 *
 * Replaces the old two-hop dance (useClassTeacher.ts): fetch the WHOLE SCHOOL's
 * teacher/class/subject assignments, filter client-side for one class-teacher row,
 * then a second round trip to resolve that one Employee's UserId. That only ever
 * resolved the single class teacher; this resolves every teacher of the class in
 * one server-side, already-authorized call (GET ParentCommunication/{classId}/teachers).
 */
export const useClassTeachers = (classId?: string): UseClassTeachersResult => {
  const [teachers, setTeachers] = useState<ClassTeacherContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refetch = useCallback(() => setRefreshToken((v) => v + 1), []);

  useEffect(() => {
    if (!classId) {
      setTeachers([]);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    parentCommunicationAPI
      .getClassTeachers(classId)
      .then(({ data }) => {
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setTeachers(
          list
            .map((t: any) => ({
              id: t.userId || t.UserId,
              employeeId: t.employeeId || t.EmployeeId,
              name: (t.name || t.Name || '').trim() || 'Teacher',
              isClassTeacher: !!(t.isClassTeacher ?? t.IsClassTeacher),
              subjects: t.subjects || t.Subjects || [],
              lastMessageText: t.lastMessageText || t.LastMessageText,
              lastMessageDate: t.lastMessageDate || t.LastMessageDate,
              unreadCount: t.unreadCount ?? t.UnreadCount ?? 0,
            }))
            .filter((t: ClassTeacherContact) => !!t.id),
        );
      })
      .catch((err) => {
        Logger.error('Chat', 'Failed to load class teachers', err);
        if (isMounted) setError('Failed to load teachers');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [classId, refreshToken]);

  const classTeacher = teachers.find((t) => t.isClassTeacher) || null;

  return { teachers, classTeacher, loading, error, refetch };
};
